/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { Card, Loader, SimpleGrid, Stack } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import { useIntl } from "react-intl";
import { useTRPC } from "../../../trpc";
import { MapItem } from "./components/MapItem";
import { type FC, type JSX, useMemo, useState } from "react";
import { useLocalStorage, useDebouncedValue } from "@mantine/hooks";
import { MapListControls } from "./components/MapListControls";
import { MapItemCondensed } from "./components/MapItemCondensed";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { Style, StyleVersion, StyleWithVersion } from "./types/map";
import { useSessionAccountName } from "../../../hooks/user";

interface SortableVersion {
  version: {
    updatedAt: string | null;
    createdAt: string | null;
  };
  style: Style;
}

function sortByDate(a: SortableVersion, b: SortableVersion) {
  const aDate = a.version.updatedAt ?? a.version.createdAt;
  const bDate = b.version.updatedAt ?? b.version.createdAt;
  if (aDate && bDate) {
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  }
  return 0;
}

function sortStyles<T extends SortableVersion>(styles: T[]): T[] {
  const publicStyles = styles.filter(s => s.style.scope === "public").sort(sortByDate);
  const privateStyles = styles.filter(s => s.style.scope === "private").sort(sortByDate);
  return [...privateStyles, ...publicStyles];
}

interface MapListProps {
  isLoading: boolean;
  isCondensedView: boolean;
  emptyListCard: JSX.Element;
  filteredMaps: StyleWithVersion[];
}

const MapList: FC<MapListProps> = ({
  isLoading,
  filteredMaps,
  emptyListCard,
  isCondensedView,
}) => {
  if (isLoading) {
    return <Loader />;
  }
  if (filteredMaps.length === 0) {
    return emptyListCard;
  }
  if (isCondensedView) {
    return (
      <Stack gap="xs">
        {filteredMaps.map(({ style, version, account }) => (
          <MapItemCondensed
            key={style.id}
            account={account}
            style={style}
            version={version as unknown as StyleVersion}
          />
        ))}
      </Stack>
    );
  }
  return (
    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
      {filteredMaps.map(({ style, version, account }) => (
        <MapItem
          key={style.id}
          account={account}
          style={style}
          version={version as unknown as StyleVersion}
        />
      ))}
    </SimpleGrid>
  );
};

export function Maps() {
  const intl = useIntl();
  const trpc = useTRPC();
  const navigate = useNavigate();
  const currentAccountName = useSessionAccountName();

  const {
    data: styles = [],
    isLoading,
  } = useQuery(trpc.getStylesWithCurrentVersion.queryOptions(undefined, {
    refetchInterval: 1000 * 60 * 5,
  }));
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(searchQuery, 200);
  const [isCondensedView, setIsCondensedView] = useLocalStorage({
    key: "map-view-mode",
    defaultValue: false,
    getInitialValueInEffect: false,
  });

  const toggleView = () => {
    setIsCondensedView(!isCondensedView);
  };

  const filteredMaps = useMemo(() => {
    if (!debouncedQuery.trim()) return sortStyles(styles);

    const query = debouncedQuery.toLowerCase().trim();
    const filtered = styles
      .filter(({ style }) => {
        const { displayName, description } = style;

        if (displayName.toLowerCase().includes(query)) return true;
        if (description?.toLowerCase().includes(query)) return true;

        return false;
      });
    return sortStyles(filtered);
  }, [styles, debouncedQuery]);

  const emptyListCard = useMemo(() => (
    <Card withBorder p="xl" ta="center">
      {debouncedQuery
        ? (
            intl.formatMessage({
              defaultMessage: "No maps found matching \"{query}\"",
              id: "text.noMapsFound",
            }, { query: debouncedQuery })
          )
        : (
            intl.formatMessage({
              defaultMessage: "No maps available. Create your first map!",
              id: "text.noMaps",
            })
          )}
    </Card>
  ), [intl, debouncedQuery]);

  return (
    <Stack gap={16}>
      <SimpleBreadcrumbs
        parent={intl.formatMessage({
          defaultMessage: "Home",
          id: "navigation.home",
        })}
        current={intl.formatMessage({
          defaultMessage: "Maps",
          id: "navigation.maps",
        })}
        description={intl.formatMessage({
          defaultMessage: "Manage your maps. Create, customize, and share maps for your applications.",
          id: "text.mapsDescription",
        })}
      />
      <MapListControls
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isCondensedView={isCondensedView}
        toggleView={toggleView}
        onCreateNew={() => {
          void navigate({
            to: "/$accountName/new-map",
            params: { accountName: currentAccountName ?? "" },
          });
        }}
      />

      <MapList
        isLoading={isLoading}
        isCondensedView={isCondensedView}
        emptyListCard={emptyListCard}
        filteredMaps={filteredMaps as unknown as StyleWithVersion[]}
      />
    </Stack>
  );
}
